import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { API_BASE_URL, getImageUrl } from '../../config';
import './AdminOverview.css';
import { formatDateTime } from '../../lib/formatDate';

/**
 * Admin Overview — system-wide stats panel.
 *
 * Cheap single-fetch view: stats, top 5 tools, 14-day DAU sparkline,
 * and the busiest departments. No interactive controls — purely a
 * dashboard. Refresh by re-mounting (tab switch back).
 */
export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    axios
      .get(`${API_BASE_URL}/api/admin/overview`, { withCredentials: true })
      .then((r) => {
        if (active) setData(r.data);
      })
      .catch(() => {
        if (active) setError('Could not load overview.');
      });
    return () => {
      active = false;
    };
  }, []);

  if (error) {
    return <div className="ao-error" role="alert">{error}</div>;
  }
  if (!data) {
    return (
      <div className="ao-skeleton" aria-busy="true">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="ao-skel-card" />
        ))}
      </div>
    );
  }

  const s = data.stats;

  return (
    <div className="ao">
      <section className="ao-grid" aria-label="People">
        <StatCard tone="primary" label="Active users" value={s.users_active} hint={`${s.users_total} total · ${s.users_never_signed_in} never signed in`} />
        <StatCard tone="success" label="Daily active" value={s.dau} hint="Clicked a tool in last 24h" />
        <StatCard tone="info" label="Weekly active" value={s.wau} hint="Last 7 days" />
        <StatCard tone="info" label="Monthly active" value={s.mau} hint="Last 30 days" />
        <StatCard tone="muted" label="Admins" value={s.users_admin} hint={`${s.users_dept_managers} dept ${s.users_dept_managers === 1 ? 'manager' : 'managers'}`} />
      </section>

      <section className="ao-row">
        <div className="ao-card">
          <h3>Engagement</h3>
          <div className="ao-engagement">
            <Metric value={s.clicks_1d} label="clicks today" />
            <Metric value={s.clicks_7d} label="last 7d" />
            <Metric value={s.clicks_30d} label="last 30d" />
          </div>
          <ActivityChart points={data.dau_sparkline} />
        </div>

        <div className="ao-card">
          <h3>Requests &amp; coverage</h3>
          <div className="ao-subgroup">
            <h4 className="ao-subhead">Tool requests</h4>
            <div className="ao-engagement">
              <Metric value={s.requests_pending} label="pending" warn={s.requests_pending > 0} />
              <Metric value={s.requests_approved} label="approved" />
              <Metric value={s.requests_rejected} label="rejected" />
            </div>
            <p className="ao-caption">
              {s.requests_7d} new in last 7d · {s.requests_30d} in last 30d
            </p>
          </div>
          <div className="ao-subdivider" aria-hidden="true" />
          <div className="ao-subgroup">
            <h4 className="ao-subhead">App requests</h4>
            <div className="ao-engagement">
              <Metric value={s.app_requests_pending} label="pending" warn={s.app_requests_pending > 0} />
              <Metric value={s.app_requests_provisioning} label="provisioning" />
              <Metric value={s.app_requests_inprogress} label="in progress" />
            </div>
            <div className="ao-engagement">
              <Metric value={s.app_requests_publish_review} label="publish review" warn={s.app_requests_publish_review > 0} />
              <Metric value={s.app_requests_published} label="published" />
              <Metric value={s.app_requests_rejected} label="rejected" />
            </div>
            <p className="ao-caption">
              {s.app_requests_7d} new in last 7d · {s.app_requests_30d} in last 30d
            </p>
          </div>
          <div className="ao-subdivider" aria-hidden="true" />
          <div className="ao-subgroup">
            <h4 className="ao-subhead">Coverage</h4>
            <div className="ao-engagement">
              <Metric value={s.depts_managed} label={`managed / ${s.depts_total} depts`} />
              <Metric value={s.tools_total} label="tools" />
              <Metric value={s.dept_managers_only_assignments} label="managers-only" warn={s.dept_managers_only_assignments > 0} />
            </div>
            <p className="ao-caption">
              {s.depts_total - s.depts_managed} department{s.depts_total - s.depts_managed === 1 ? '' : 's'} without a manager
            </p>
          </div>
        </div>
      </section>

      <section className="ao-row">
        <div className="ao-card ao-card--wide">
          <h3>Top tools · last 30 days</h3>
          {data.top_tools.length === 0 ? (
            <p className="ao-empty">No tool clicks yet. Engagement starts being tracked once users open tools.</p>
          ) : (
            <ol className="ao-toplist">
              {data.top_tools.map((t, idx) => (
                <li key={t.id}>
                  <span className="ao-rank">{idx + 1}</span>
                  <span className="ao-tool-icon" aria-hidden="true">
                    {t.icon_url ? <img src={getImageUrl(t.icon_url)} alt="" /> : <em>{t.name.slice(0, 2).toUpperCase()}</em>}
                  </span>
                  <span className="ao-tool-name">{t.name}</span>
                  <span className="ao-tool-stat">
                    <strong>{t.clicks}</strong> clicks · {t.users} {t.users === 1 ? 'user' : 'users'}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="ao-card ao-card--wide">
          <h3>Departments by team size</h3>
          {data.top_departments.length === 0 ? (
            <p className="ao-empty">No departments yet.</p>
          ) : (
            <ul className="ao-deptlist">
              {data.top_departments.map((d) => (
                <li key={d.id}>
                  <span className="ao-dept-name">{d.name}</span>
                  <span className={`ao-dept-manager ${d.has_manager ? '' : 'is-unmanaged'}`}>
                    {d.has_manager
                      ? (d.manager_count > 1 ? `Managed (${d.manager_count})` : 'Managed')
                      : 'Unmanaged'}
                  </span>
                  <span className="ao-dept-count">{d.member_count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="ao-row">
        <div className="ao-card ao-card--wide">
          <h3>Tools by type</h3>
          {(!data.tool_types || data.tool_types.length === 0) ? (
            <p className="ao-empty">No tools yet.</p>
          ) : (
            <ul className="ao-typelist">
              {data.tool_types.map((t) => (
                <li key={t.type}>
                  <span className={`ao-type-badge ao-type-badge--${t.type}`}>
                    {t.type.charAt(0).toUpperCase() + t.type.slice(1)}
                  </span>
                  <span className="ao-type-count">{t.count}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="ao-caption">
            {s.tools_total} tool{s.tools_total === 1 ? '' : 's'} total across {data.tool_types ? data.tool_types.filter((t) => t.count > 0).length : 0} type{(data.tool_types && data.tool_types.filter((t) => t.count > 0).length === 1) ? '' : 's'}
          </p>
        </div>
      </section>

      {data.odoo && <OdooSyncOverview odoo={data.odoo} />}
    </div>
  );
}

function StatCard({ tone, label, value, hint }) {
  return (
    <div className={`ao-stat ao-stat--${tone}`}>
      <span className="ao-stat-value">{value}</span>
      <span className="ao-stat-label">{label}</span>
      {hint && <span className="ao-stat-hint">{hint}</span>}
    </div>
  );
}

function Metric({ value, label, warn }) {
  return (
    <div className={`ao-metric ${warn ? 'is-warn' : ''}`}>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}


/**
 * Odoo sync snapshot card. The DB-backed counts + last-run summary arrive
 * instantly in the overview payload. Live connection reachability is fetched
 * separately (and lazily) from the cheap status probe so it never blocks the
 * overview load. Hidden entirely when Odoo isn't configured (the parent guards
 * on `data.odoo`).
 */
function OdooSyncOverview({ odoo }) {
  // null = still probing; 'reachable' | 'unreachable' once known.
  const [reach, setReach] = useState(null);

  useEffect(() => {
    if (!odoo.configured) return undefined;
    let active = true;
    axios
      .get(`${API_BASE_URL}/api/admin/odoo-sync/status`, { withCredentials: true })
      .then((r) => {
        if (active) setReach(r.data?.odoo_reachable ? 'reachable' : 'unreachable');
      })
      .catch(() => {
        if (active) setReach('unreachable');
      });
    return () => {
      active = false;
    };
  }, [odoo.configured]);

  const lastRun = formatDateTime(odoo.last_run_at);
  const sum = odoo.last_summary || null;
  const moved = sum ? (Number(sum.reassign || 0) + Number(sum.add || 0)) : null;

  let statusTone = 'muted';
  let statusText = 'Not configured';
  if (odoo.running) { statusTone = 'info'; statusText = 'Sync running…'; }
  else if (odoo.configured && reach === null) { statusTone = 'muted'; statusText = 'Checking…'; }
  else if (odoo.configured && reach === 'reachable') { statusTone = 'success'; statusText = 'Connected'; }
  else if (odoo.configured && reach === 'unreachable') { statusTone = 'warn'; statusText = 'Unreachable'; }

  return (
    <section className="ao-row">
      <div className="ao-card ao-card--wide">
        <div className="ao-card-head">
          <h3>Odoo department sync</h3>
          <span className={`ao-odoo-status ao-odoo-status--${statusTone}`}>{statusText}</span>
        </div>
        <div className="ao-engagement">
          <Metric value={odoo.depts_mirrored} label="mirrored depts" />
          <Metric value={odoo.custom_mappings} label="custom mappings" />
          <Metric value={odoo.odoo_sourced_depts} label="Odoo-sourced" />
        </div>
        <p className="ao-caption">
          {lastRun
            ? <>Last sync {lastRun}{moved != null ? ` · ${moved} member${moved === 1 ? '' : 's'} moved` : ''}.</>
            : 'No sync has run since the server started.'}
        </p>
      </div>
    </section>
  );
}

const AO_BAR = '#94a3b8';
const AO_BAR_TODAY = 'var(--ao-accent)';

function formatShortDay(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}
function formatDow(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { weekday: 'short' });
  } catch {
    return '';
  }
}

function ActivityTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0].payload;
  return (
    <div className="ao-chart-tooltip">
      <span className="ao-chart-tooltip-date">{formatShortDay(p.day)}</span>
      <span className="ao-chart-tooltip-value">
        {p.users} active user{p.users === 1 ? '' : 's'}
      </span>
    </div>
  );
}

function ActivityChart({ points }) {
  if (!points || points.length === 0) return null;

  const n = points.length;
  const todayIdx = n - 1;
  const todayValue = points[todayIdx].users;

  const thisWeek = points.slice(-7).reduce((sum, p) => sum + p.users, 0);
  const prevWeek = points.slice(-14, -7).reduce((sum, p) => sum + p.users, 0);
  const deltaPct = prevWeek === 0
    ? (thisWeek === 0 ? 0 : 100)
    : Math.round(((thisWeek - prevWeek) / prevWeek) * 100);
  const deltaSign = thisWeek === prevWeek ? 'flat' : thisWeek > prevWeek ? 'up' : 'down';

  // Recharts consumes plain objects; carry an isToday flag for per-bar colouring
  // and a short label for the axis.
  const chartData = points.map((p, i) => ({
    ...p,
    isToday: i === todayIdx,
    label: formatDow(p.day),
  }));

  const max = Math.max(1, ...points.map((p) => p.users));
  const yMax = max <= 4 ? Math.max(2, max) : Math.ceil(max / 2) * 2;

  return (
    <div className="ao-chart">
      <div className="ao-chart-headline">
        <div className="ao-chart-headline-main">
          <span className="ao-chart-today">{todayValue}</span>
          <span className="ao-chart-today-label">active today</span>
        </div>
        <div className={`ao-chart-delta ao-chart-delta--${deltaSign}`}>
          <span className="ao-chart-delta-arrow" aria-hidden="true">
            {deltaSign === 'up' ? '▲' : deltaSign === 'down' ? '▼' : '◆'}
          </span>
          <span className="ao-chart-delta-value">
            {deltaSign === 'flat' ? '0%' : `${deltaPct > 0 ? '+' : ''}${deltaPct}%`}
          </span>
          <span className="ao-chart-delta-label">vs prior 7d</span>
        </div>
      </div>

      <div className="ao-chart-plot" aria-label="Daily active users · last 14 days" role="img">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 12, right: 8, bottom: 4, left: -16 }}>
            <CartesianGrid vertical={false} stroke="#eef0f4" strokeDasharray="2 4" />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={{ stroke: '#cbd5e1' }}
              tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 600 }}
              interval="preserveStartEnd"
              minTickGap={12}
            />
            <YAxis
              allowDecimals={false}
              domain={[0, yMax]}
              tickLine={false}
              axisLine={false}
              width={32}
              tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 600 }}
            />
            <Tooltip
              cursor={{ fill: 'rgba(148, 163, 184, 0.12)' }}
              content={<ActivityTooltip />}
            />
            <Bar dataKey="users" radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false}>
              {chartData.map((p) => (
                <Cell key={p.day} fill={p.isToday ? AO_BAR_TODAY : AO_BAR} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <p className="ao-caption">Daily active users · last 14 days · hover a bar for details</p>
    </div>
  );
}
